<template>
  <main class="single-film-prototype single-show-prototype single-person-prototype single-film-list-prototype">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        :background-src="defaultBackgroundImage"
        background-wrap-class="film-list-hero__bgWrap"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end film-list-hero__grid">
          <div class="col hero__meta film-list-hero__meta">
            <div class="upper-container">
              <div class="user-event-container film-list-hero__meta-stack">
                <input
                  v-if="listEditMode"
                  v-model="editableListTitle"
                  class="item-title film-list-detail__title-input"
                  data-testid="show-list-title-input"
                  type="text"
                >
                <h1 v-else class="item-title" data-testid="show-list-title">
                  {{ showList.title || '' }}
                </h1>
                <p
                  v-if="showListLoaded && !showListError"
                  class="film-list-hero__meta-line"
                  data-testid="show-list-meta"
                >
                  {{ showCountLabel }}
                  <span aria-hidden="true"> • </span>
                  <CuratedListLabel v-if="showList.is_curated" tag="span" />
                  <template v-else>
                    <span>List by </span>
                    <a data-testid="show-list-owner-link" :href="showListOwnerPath">{{ showList.owner_name || 'Unknown' }}</a>
                  </template>
                  <span aria-hidden="true" class="film-list-detail__meta-separator--updated"> • </span>
                  <span class="film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
                </p>
                <div v-if="listFavouritesEnabled && !listEditMode" class="buttons film-list-hero__buttons">
                  <template v-if="!isAuthenticated">
                    <a class="btn single-btn person-fav-btn" data-testid="show-list-favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </a>
                  </template>
                  <template v-else>
                    <button
                      class="btn single-btn person-fav-btn film-list-hero__favourite-button"
                      data-testid="show-list-favourite-action"
                      type="button"
                      title="List favourites are not available yet."
                    >
                      <div class="default-btn">
                        <i class="ion-heart"></i> Add to favourites
                      </div>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <section class="mobile g-0 film-list-mobile-meta container">
      <div class="row g-0 item-title-mobile">
        <input
          v-if="listEditMode"
          v-model="editableListTitle"
          class="film-list-detail__title-input film-list-detail__title-input--mobile"
          data-testid="show-list-title-input-mobile"
          type="text"
        >
        <h1 v-else data-testid="show-list-title-mobile">{{ showList.title || '' }}</h1>
      </div>
      <div v-if="showListLoaded && !showListError" class="row g-0">
        <p class="film-list-hero__meta-line film-list-hero__meta-line--mobile">
          {{ showCountLabel }}
          <span aria-hidden="true"> • </span>
          <CuratedListLabel v-if="showList.is_curated" tag="span" />
          <template v-else>
            <span>List by </span>
            <a data-testid="show-list-owner-link-mobile" :href="showListOwnerPath">{{ showList.owner_name || 'Unknown' }}</a>
          </template>
          <span aria-hidden="true" class="film-list-detail__meta-separator--updated"> • </span>
          <span class="film-list-detail__meta-updated">Updated {{ updatedAtLabel }}</span>
        </p>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div v-if="listFavouritesEnabled && !listEditMode" class="buttons">
            <template v-if="!isAuthenticated">
              <a class="btn single-btn" data-testid="show-list-favourite-action-mobile" :href="loginPath">
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </a>
            </template>
            <template v-else>
              <button
                class="btn single-btn film-list-hero__favourite-button"
                data-testid="show-list-favourite-action-mobile"
                type="button"
                title="List favourites are not available yet."
              >
                <div class="default-btn">
                  <i class="ion-heart"></i> Add to favourites
                </div>
              </button>
            </template>
          </div>
        </div>
      </div>
    </section>

    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav">
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'overview' }"
                  :to="overviewPath"
                  data-testid="show-list-overview-tab"
                >
                  Overview
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'comments' }"
                  :to="commentsPath"
                  data-testid="show-list-comments-tab"
                >
                  Comments
                </RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div v-if="activeSection === 'overview'" class="container g-0">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0">
            <div class="row g-0">
              <div class="col g-0">
                <div class="container g-0">
                  <div class="row g-0">
                    <div
                      v-if="listEditMode || showList.description || showListLoading || showListError"
                      class="col g-0 bio"
                    >
                      <template v-if="showListLoaded && !showListError && listEditMode">
                        <p
                          class="film-list-detail__description-label"
                          data-testid="show-list-description-label"
                        >
                          Overview
                        </p>
                        <textarea
                          v-model="editableListDescription"
                          class="film-list-detail__description-textarea"
                          data-testid="show-list-description-textarea"
                          rows="5"
                        />
                      </template>
                      <p
                        v-else-if="showListLoaded && !showListError && showList.description"
                        class="film-list-page__description"
                        data-testid="show-list-description"
                      >
                        {{ showList.description }}
                      </p>
                      <p v-else-if="showListLoading" class="film-lists-empty" data-testid="show-list-loading">
                        Loading list...
                      </p>
                      <p v-else-if="showListError" class="film-lists-empty" data-testid="show-list-error">
                        {{ showListError }}
                      </p>
                    </div>
                  </div>
                </div>

                <template v-if="showListLoaded && !showListError">
                  <div
                    ref="listContentStartRef"
                    class="row g-0 sorting-buttons-row"
                    :class="{ 'sorting-buttons-row--list-editing': listEditMode }"
                  >
                    <div class="col g-0">
                      <div class="sorting-buttons">
                        <span class="sort-title">Sort:</span>
                        <div class="sorting-buttons__primary">
                          <a
                            v-for="button in primarySortButtons"
                            :key="button.key"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            @click.prevent="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </a>
                        </div>
                        <div v-if="secondarySortButtons.length" class="sorting-buttons__secondary">
                          <a
                            v-for="button in secondarySortButtons"
                            :key="button.key"
                            :class="[
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            @click.prevent="setSortMode(button.key)"
                          >
                            {{ button.label }}
                          </a>
                        </div>
                        <div class="sorting-buttons__desktop-secondary-row">
                          <div class="sorting-buttons__title">
                            <a
                              v-for="button in titleSortButtons"
                              :key="button.key"
                              :class="[
                                'sorting-btn',
                                button.active ? 'active' : null,
                              ]"
                              href="#"
                              @click.prevent="setSortMode(button.key)"
                            >
                              {{ button.label }}
                            </a>
                          </div>
                          <a
                            v-for="button in viewModeButtons"
                            :key="`desktop-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            data-testid="show-list-desktop-view-mode-button"
                            href="#"
                            :aria-label="button.label"
                            :title="button.label"
                            @click.prevent="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" data-testid="show-list-view-mode-icon" />
                          </a>
                        </div>
                        <div
                          class="modal"
                          data-testid="show-list-filter-modal"
                          :style="{ display: filtersModalOpen ? 'flex' : 'none' }"
                          @click.self="toggleFilters"
                        >
                          <button
                            class="person-compact-rating-picker__close person-filter-modal__close"
                            data-testid="show-list-filter-modal-close"
                            type="button"
                            aria-label="Close filters"
                            @click="toggleFilters"
                          >
                            <span class="person-compact-rating-picker__close-icon ion-close" aria-hidden="true"></span>
                          </button>
                          <div class="container person-filters">
                            <div class="person-filter-modal__header">
                              <h2>Filters:</h2>
                              <button
                                class="sorting-btn person-filter-modal__apply person-filter-modal__apply-mobile"
                                data-testid="show-list-filter-modal-apply"
                                type="button"
                                @click="toggleFilters"
                              >
                                Apply
                              </button>
                            </div>
                            <div
                              v-for="row in filterModalRows"
                              :key="row.key"
                              class="row g-0 filters-groups-row person-filter-modal__row"
                            >
                              <div
                                v-for="group in row.groups"
                                :key="group.key"
                                class="person-filter-modal__group person-filter-modal__group--half col-6"
                                data-testid="show-list-filter-modal-group"
                              >
                                <div class="single-filter-title-row">
                                  <h3>{{ group.title }}</h3>
                                </div>
                                <div class="filter-row">
                                  <a
                                    v-for="option in group.options"
                                    :key="option.value"
                                    :class="{ active: option.active }"
                                    href="#"
                                    @click.prevent="applyFilter(group.key, option.value)"
                                  >
                                    {{ option.label }}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="view-mode-btns">
                          <a
                            v-for="button in viewModeButtons"
                            :key="`mobile-${button.key}`"
                            :class="[
                              button.first ? 'first-btn' : null,
                              'view-mode-btn',
                              'sorting-btn',
                              button.active ? 'active' : null,
                            ]"
                            href="#"
                            :aria-label="button.label"
                            :title="button.label"
                            @click.prevent="setViewMode(button.key)"
                          >
                            <ViewModeIcon :mode="button.key" />
                          </a>
                          <button
                            class="sorting-btn filter-btn view-mode-filter-btn"
                            data-testid="show-list-mobile-filter-button"
                            type="button"
                            @click="toggleFilters"
                          >
                            {{ filterButtonLabel }}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="!reorderMode" class="row g-0 filters-section-sm">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div class="container g-0">
                          <div class="row g-0">
                            <div class="col-9 g-0 filtering-row">
                              <input
                                v-model="titleFilter"
                                class="filmography-filter"
                                data-testid="show-list-filter-input"
                                type="search"
                                placeholder="Filter by title or start year..."
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="showListCanEdit"
                    class="row g-0 list-addition-row film-list-detail__edit-row"
                  >
                    <div class="col g-0">
                      <button
                        v-if="!reorderMode"
                        class="btn-sm list-addition-btn"
                        data-testid="show-list-edit-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startListEditing"
                      >
                        {{ listEditMode ? 'Stop editing' : 'Edit list' }}
                      </button>
                      <label
                        v-if="listEditMode"
                        class="film-list-detail__public-edit"
                        data-testid="show-list-public-edit-field"
                      >
                        <input
                          v-model="editableListPublic"
                          data-testid="show-list-public-edit"
                          type="checkbox"
                        >
                        <span>Public</span>
                      </label>
                      <button
                        v-if="!listEditMode && !reorderMode"
                        class="btn-sm"
                        data-testid="show-list-reorder-button"
                        type="button"
                        :disabled="resetNumbersPending"
                        @click="startReordering"
                      >
                        Reorder items
                      </button>
                      <ResetListNumbers
                        v-if="!listEditMode && !reorderMode"
                        kind="show"
                        :pending="resetNumbersPending"
                        :notice="resetNumbersNotice"
                        @reset="resetNumbers"
                      />
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="show-list-reorder-done-button"
                        type="button"
                        @click="finishReordering"
                      >
                        Done reordering
                      </button>
                      <button
                        v-if="reorderMode"
                        class="btn-sm"
                        data-testid="show-list-reorder-save-button"
                        type="button"
                        @click="saveReordering"
                      >
                        Save new order
                      </button>
                      <button
                        v-if="reorderMode && hasReorderChanges"
                        class="btn-sm film-list-detail__discard-button"
                        data-testid="show-list-reorder-discard-button"
                        type="button"
                        @click="discardReorderChanges"
                      >
                        Discard changes
                      </button>
                      <p
                        v-if="reorderMode"
                        class="film-list-detail__reorder-instructions"
                        data-testid="show-list-reorder-instructions"
                      >
                        Use the arrow buttons to move items up or down.
                      </p>
                      <div v-if="listEditMode" class="film-list-detail__edit-actions">
                        <button
                          class="btn-sm"
                          data-testid="show-list-save-button"
                          type="button"
                          @click="saveListEditing"
                        >
                          Save
                        </button>
                        <button
                          v-if="hasUnsavedChanges"
                          class="btn-sm film-list-detail__discard-button"
                          data-testid="show-list-discard-button"
                          type="button"
                          @click="discardListEditing"
                        >
                          Discard changes
                        </button>
                      </div>
                      <p
                        v-if="showUnsavedChangesNotice"
                        class="film-list-detail__edit-warning"
                        data-testid="show-list-unsaved-notice"
                      >
                        You have unsaved changes. Save or discard the changes.
                      </p>
                      <p
                        v-if="showSavedChangesNotice"
                        class="film-list-detail__edit-success"
                        data-testid="show-list-saved-notice"
                        @click="dismissSavedChangesNotice"
                      >
                        Changes saved.
                      </p>
                      <div v-if="listEditMode" class="film-list-detail__add-item-panel">
                        <p class="film-list-detail__add-item-title" data-testid="show-list-add-item-title">
                          Add new list item
                        </p>
                        <div class="top-search-autocomplete film-list-detail__add-item-search">
                          <div class="top-search-autocomplete__field">
                            <input
                              v-model="addItemQuery"
                              class="top-search form-control"
                              data-testid="show-list-add-item-input"
                              type="search"
                              placeholder="Search shows..."
                              @input="handleAddItemSearchInput"
                              @keydown.down.prevent="highlightNextAddItemResult"
                              @keydown.up.prevent="highlightPreviousAddItemResult"
                              @keydown.enter.prevent="handleAddItemEnter"
                              @keydown.esc="resetAddItemSearch"
                            >
                            <div
                              v-if="addItemSearchResults.length > 0"
                              class="top-search-autocomplete__results"
                              data-testid="show-list-add-item-results"
                            >
                              <button
                                v-for="(result, index) in addItemSearchResults"
                                :key="`show-list-add-item-${result.id}`"
                                :ref="(element) => setAddItemResultRef(element, index)"
                                :class="[
                                  'top-search-autocomplete__result',
                                  'film-list-detail__add-item-result',
                                  {
                                    'top-search-autocomplete__result--active': index === addItemActiveIndex,
                                    'top-search-autocomplete__result--disabled': isShowAddItemDisabled(result.id),
                                  },
                                ]"
                                :disabled="isShowAddItemDisabled(result.id)"
                                data-testid="show-list-add-item-result"
                                type="button"
                                @mouseenter="setSelectableAddItemActiveIndex(result, index)"
                                @focus="setSelectableAddItemActiveIndex(result, index)"
                                @click="addSearchResult(result)"
                              >
                                <span class="top-search-autocomplete__label">
                                  <span class="top-search-autocomplete__keyword">{{ result.title }}</span>
                                  <span
                                    v-if="isShowAlreadyOnList(result.id)"
                                    class="top-search-autocomplete__year"
                                  >
                                    (already on the list)
                                  </span>
                                  <span
                                    v-if="result.year"
                                    class="top-search-autocomplete__year"
                                  >
                                    ({{ result.year }})
                                  </span>
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                        <p
                          v-if="addItemNotice"
                          class="film-list-detail__edit-success"
                          data-testid="show-list-add-item-notice"
                          @click="addItemNotice = ''"
                        >
                          {{ addItemNotice }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div class="container g-0">
                    <div class="row g-0">
                      <div class="col g-0">
                        <div
                          v-if="reorderMode"
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list film-list-detail__reorder-list"
                          data-testid="show-list-reorder-list"
                        >
                          <p
                            v-if="reorderMoveNotice.position === 'above'"
                            class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--above"
                            data-testid="show-list-reorder-notice-above"
                          >
                            {{ reorderMoveNotice.text }}
                          </p>
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="show-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="(item, index) in reorderPaginatedItems"
                            v-else
                            :key="item.id"
                            :class="[
                              'row',
                              'g-0',
                              'person-text-only-credit-row',
                              'film-list-detail__text-row',
                              index === reorderPaginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                            ]"
                            data-testid="show-list-reorder-row"
                          >
                            <div class="col g-0">
                              <div class="person-text-only-row film-list-detail__text-row-top">
                                <span
                                  class="film-list-detail__text-number film-list-detail__reorder-number"
                                  data-testid="show-list-reorder-number"
                                >
                                  {{ reorderDisplayNumber(index) }}
                                </span>
                                <div class="film-list-detail__reorder-position-actions">
                                  <span
                                    v-if="isFirstReorderItem(item.id)"
                                    class="film-list-detail__reorder-position-placeholder"
                                    aria-hidden="true"
                                  ></span>
                                  <button
                                    v-else
                                    class="btn-sm film-list-detail__reorder-position-button"
                                    data-testid="show-list-reorder-move-up"
                                    type="button"
                                    title="Move item up"
                                    aria-label="Move item up"
                                    @click="moveReorderItem(item.id, -1)"
                                  >
                                    ↑
                                  </button>
                                  <button
                                    v-if="!isLastReorderItem(item.id)"
                                    class="btn-sm film-list-detail__reorder-position-button"
                                    data-testid="show-list-reorder-move-down"
                                    type="button"
                                    title="Move item down"
                                    aria-label="Move item down"
                                    @click="moveReorderItem(item.id, 1)"
                                  >
                                    ↓
                                  </button>
                                </div>
                                <a
                                  :href="item.show.url || '#'"
                                  class="film-list-detail__text-title-link"
                                  data-testid="show-list-text-title-link"
                                >
                                  <h3 class="person-text-only-credit-film-title">
                                    {{ truncatedShowTitle(item.show.title) }}
                                  </h3>
                                </a>
                                <span v-if="item.show.year" class="person-text-only-release-year">
                                  <span class="person-text-only-parenthesis">(</span>{{ item.show.year }}<span class="person-text-only-parenthesis">)</span>
                                </span>
                              </div>
                            </div>
                          </div>
                          <p
                            v-if="reorderMoveNotice.position === 'below'"
                            class="film-list-detail__reorder-move-notice film-list-detail__reorder-move-notice--below"
                            data-testid="show-list-reorder-notice-below"
                          >
                            {{ reorderMoveNotice.text }}
                          </p>
                        </div>

                        <div
                          v-else-if="currentViewMode === '1'"
                          class="container g-0 credits-list film-list-detail__credits-list"
                          data-testid="show-list-full-view"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="show-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <template v-for="(item, index) in paginatedItems" :key="item.id">
                            <div
                              v-if="listEditMode && isListItemPendingRemoval(item.id)"
                              class="row g-0 film-list-detail__removed-placeholder-wrap"
                              data-testid="show-list-item-removed-placeholder-wrap"
                            >
                              <div class="col g-0">
                                <div class="film-list-detail__removed-placeholder" data-testid="show-list-item-removed-placeholder">
                                  <span class="film-list-detail__removed-placeholder-text">
                                    '{{ item.show.title }}' is to be deleted on save.
                                  </span>
                                  <button
                                    class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                    data-testid="show-list-item-undo-remove-button"
                                    type="button"
                                    @click="undoListItemRemoval(item.id)"
                                  >
                                    Undo
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!(listEditMode && isListItemPendingRemoval(item.id))"
                              class="row g-0 single-credit-row film-list-detail__row"
                              data-testid="show-list-item-row"
                            >
                              <div class="col-3 g-0 film-poster-col film-list-detail__poster-col">
                                <p class="film-list-detail__number" data-testid="show-list-item-number">
                                  {{ item.number || '–' }}
                                </p>
                                <div class="film-poster-container">
                                  <a :href="item.show.url || '#'">
                                    <img
                                      class="film-poster"
                                      :src="item.show.poster_url || posterPlaceholderImage"
                                      :alt="`${item.show.title} poster`"
                                    >
                                  </a>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="container g-0 single-credit-container film-list-detail__container">
                                  <div class="row g-0 credit-title-row">
                                    <div class="col g-0">
                                      <a :href="item.show.url || '#'">
                                        <h3 class="credit-film-title">
                                          {{ truncatedShowTitle(item.show.title) }}
                                          <span v-if="item.show.year" class="release-year">({{ item.show.year }})</span>
                                        </h3>
                                      </a>
                                    </div>
                                  </div>
                                  <div
                                    v-if="!listEditMode"
                                    class="film-list-detail__mobile-controls"
                                    data-testid="show-list-item-mobile-controls"
                                  >
                                    <div class="row g-0 film-list-detail__mobile-buttons-row">
                                      <div class="col g-0">
                                        <div
                                          class="person-buttons person-buttons--credit person-buttons--without-calendar"
                                          data-testid="show-list-item-action-group-xs"
                                        >
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="fs-btn left-btn"
                                              :class="{ 'btn--watchlisted': item.show.watchlisted }"
                                              type="button"
                                              :disabled="showWatchlistPendingBySlug[item.show.slug]"
                                              @click="toggleListItemWatchlist(item)"
                                            >
                                              <div class="default-btn">
                                                <i class="ion-bookmark"></i>
                                              </div>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="fs-btn left-btn" :href="loginPath">
                                              <div class="default-btn">
                                                <i class="ion-bookmark"></i>
                                              </div>
                                            </a>
                                          </template>
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="fs-btn right-btn"
                                              :class="{ 'btn--favourited': item.show.favourited }"
                                              type="button"
                                              :disabled="showFavouritePendingBySlug[item.show.slug]"
                                              @click="toggleListItemFavourite(item)"
                                            >
                                              <div class="default-btn">
                                                <i class="ion-heart"></i>
                                              </div>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="fs-btn right-btn" :href="loginPath">
                                              <div class="default-btn">
                                                <i class="ion-heart"></i>
                                              </div>
                                            </a>
                                          </template>
                                        </div>
                                      </div>
                                    </div>
                                    <div class="row g-0 film-list-detail__mobile-rating-row">
                                      <div class="col g-0">
                                        <div class="rating-stars film-list-detail__rating film-list-detail__rating--mobile-stack">
                                          <template v-if="isAuthenticated">
                                            <button
                                              class="person-credit-rating-summary"
                                              type="button"
                                              :disabled="showRatingPendingBySlug[item.show.slug]"
                                              @click="openRatingPicker(item)"
                                            >
                                              <i :class="listItemRatingStarClass(item)"></i>
                                            </button>
                                          </template>
                                          <template v-else>
                                            <a class="person-credit-rating-summary" :href="loginPath">
                                              <i class="rating-star ion-ios-star-outline"></i>
                                            </a>
                                          </template>
                                          <div class="current-credits-rating">
                                            <p>{{ listItemRatingText(item) }}</p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div v-if="!listEditMode" class="row g-0 film-list-detail__actions-row">
                                    <div class="col g-0">
                                      <div
                                        class="person-buttons person-buttons--credit person-buttons--without-calendar"
                                        data-testid="show-list-item-action-group"
                                      >
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="fs-btn left-btn"
                                            :class="{ 'btn--watchlisted': item.show.watchlisted }"
                                            type="button"
                                            :disabled="showWatchlistPendingBySlug[item.show.slug]"
                                            @click="toggleListItemWatchlist(item)"
                                          >
                                            <div class="default-btn">
                                              <i class="ion-bookmark"></i>
                                            </div>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn left-btn" :href="loginPath">
                                            <div class="default-btn">
                                              <i class="ion-bookmark"></i>
                                            </div>
                                          </a>
                                        </template>
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="fs-btn right-btn"
                                            :class="{ 'btn--favourited': item.show.favourited }"
                                            type="button"
                                            :disabled="showFavouritePendingBySlug[item.show.slug]"
                                            @click="toggleListItemFavourite(item)"
                                          >
                                            <div class="default-btn">
                                              <i class="ion-heart"></i>
                                            </div>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="fs-btn right-btn" :href="loginPath">
                                            <div class="default-btn">
                                              <i class="ion-heart"></i>
                                            </div>
                                          </a>
                                        </template>
                                      </div>
                                    </div>
                                    <div class="col g-0">
                                      <div class="rating-stars film-list-detail__rating">
                                        <template v-if="isAuthenticated">
                                          <button
                                            class="person-credit-rating-summary"
                                            type="button"
                                            :disabled="showRatingPendingBySlug[item.show.slug]"
                                            @click="openRatingPicker(item)"
                                          >
                                            <i :class="listItemRatingStarClass(item)"></i>
                                          </button>
                                        </template>
                                        <template v-else>
                                          <a class="person-credit-rating-summary" :href="loginPath">
                                            <i class="rating-star ion-ios-star-outline"></i>
                                          </a>
                                        </template>
                                        <div class="current-credits-rating">
                                          <p>{{ listItemRatingText(item) }}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div
                                    v-if="listEditMode || item.description"
                                    :class="[
                                      'row',
                                      'g-0',
                                      'film-list-detail__description-row',
                                      listEditMode ? 'film-list-detail__description-row--editing' : null,
                                    ]"
                                  >
                                    <div class="col g-0">
                                      <textarea
                                        v-if="listEditMode"
                                        v-model="editableItemDescriptions[item.id]"
                                        class="film-list-detail__description-textarea film-list-detail__item-description-textarea"
                                        data-testid="show-list-item-description-textarea"
                                        rows="5"
                                      />
                                      <p v-else class="film-list-detail__description-text">{{ item.description }}</p>
                                      <div
                                        v-if="listEditMode"
                                        class="film-list-detail__item-remove-row"
                                      >
                                        <button
                                          class="btn-sm film-list-detail__discard-button film-list-detail__item-remove-button"
                                          data-testid="show-list-item-remove-button"
                                          type="button"
                                          @click="removeListItemFromEditing(item.id)"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!listEditMode && !isListItemPendingRemoval(item.id)"
                              :class="[
                                'row',
                                'g-0',
                                'film-list-detail__actions-row-sm',
                                'person-credits-button-row-sm',
                                index === paginatedItems.length - 1 ? 'last-person-credits-button-row-sm' : null,
                              ]"
                              data-testid="show-list-item-action-row-sm"
                            >
                              <div class="col g-0">
                                <div
                                  class="person-buttons person-buttons--credit person-buttons--without-calendar"
                                  data-testid="show-list-item-action-group-sm"
                                >
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn left-btn"
                                      :class="{ 'btn--watchlisted': item.show.watchlisted }"
                                      type="button"
                                      :disabled="showWatchlistPendingBySlug[item.show.slug]"
                                      @click="toggleListItemWatchlist(item)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn left-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-bookmark"></i>
                                      </div>
                                    </a>
                                  </template>
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="fs-btn right-btn"
                                      :class="{ 'btn--favourited': item.show.favourited }"
                                      type="button"
                                      :disabled="showFavouritePendingBySlug[item.show.slug]"
                                      @click="toggleListItemFavourite(item)"
                                    >
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="fs-btn right-btn" :href="loginPath">
                                      <div class="default-btn">
                                        <i class="ion-heart"></i>
                                      </div>
                                    </a>
                                  </template>
                                </div>
                              </div>
                              <div class="col g-0">
                                <div class="rating-stars film-list-detail__rating film-list-detail__rating--mobile">
                                  <template v-if="isAuthenticated">
                                    <button
                                      class="person-credit-rating-summary"
                                      type="button"
                                      :disabled="showRatingPendingBySlug[item.show.slug]"
                                      @click="openRatingPicker(item)"
                                    >
                                      <i :class="listItemRatingStarClass(item)"></i>
                                    </button>
                                  </template>
                                  <template v-else>
                                    <a class="person-credit-rating-summary" :href="loginPath">
                                      <i class="rating-star ion-ios-star-outline"></i>
                                    </a>
                                  </template>
                                  <div class="current-credits-rating">
                                    <p>{{ listItemRatingText(item) }}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="!listEditMode && item.description"
                              class="row g-0 film-list-detail__description-row-sm"
                              data-testid="show-list-item-description-row-sm"
                            >
                              <div class="col g-0">
                                <p class="film-list-detail__description-text">{{ item.description }}</p>
                              </div>
                            </div>
                          </template>
                        </div>

                        <div
                          v-else-if="currentViewMode === '2'"
                          class="person-posters-grid film-list-detail__posters-grid"
                          data-testid="show-list-posters-grid"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="show-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="item in paginatedItems"
                            v-else
                            :key="item.id"
                            class="film-list-detail__poster-card"
                            data-testid="show-list-poster-card"
                          >
                            <div class="film-list-detail__poster-media">
                              <p class="film-list-detail__poster-number">{{ item.number || '–' }}</p>
                              <a class="person-poster-link" :href="item.show.url || '#'">
                                <img
                                  class="person-poster-grid-image"
                                  :src="item.show.poster_url || posterPlaceholderImage"
                                  :alt="`${item.show.title} poster`"
                                >
                              </a>
                            </div>
                          </div>
                        </div>

                        <div
                          v-else
                          class="container g-0 person-text-only-credits-list film-list-detail__text-list"
                          data-testid="show-list-text-only-list"
                        >
                          <p
                            v-if="paginatedItems.length === 0"
                            :class="{ 'person-no-results': showNoResults }"
                            data-testid="show-list-no-items"
                          >
                            {{ noItemsMessage }}
                          </p>
                          <div
                            v-for="(item, index) in paginatedItems"
                            v-else
                            :key="item.id"
                            :class="[
                              'row',
                              'g-0',
                              'person-text-only-credit-row',
                              'film-list-detail__text-row',
                              index === paginatedItems.length - 1 ? 'person-text-only-credit-row--last' : null,
                            ]"
                            data-testid="show-list-text-row"
                          >
                            <div class="col g-0">
                              <div class="person-text-only-row film-list-detail__text-row-top">
                                <span class="film-list-detail__text-number">{{ item.number || '–' }}</span>
                                <a
                                  :href="item.show.url || '#'"
                                  class="film-list-detail__text-title-link"
                                  data-testid="show-list-text-title-link"
                                >
                                  <h3 class="person-text-only-credit-film-title">
                                    {{ truncatedShowTitle(item.show.title) }}
                                  </h3>
                                </a>
                                <span v-if="item.show.year" class="release-year person-text-only-release-year">
                                  <span class="person-text-only-parenthesis">(</span>{{ item.show.year }}<span class="person-text-only-parenthesis">)</span>
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <nav
                          v-if="effectiveNumPages > 1"
                          class="film-lists-pagination film-list-detail__pagination film-list-detail__pagination--bootstrap film-list-detail__pagination--with-mobile"
                          aria-label="Show list pagination"
                        >
                          <ul class="pagination mb-0">
                            <li class="page-item" :class="{ disabled: currentPage <= 1 }">
                              <button class="page-link" data-testid="show-list-prev" type="button" @click="goToPage(currentPage - 1)">Previous</button>
                            </li>
                            <li v-for="pageItem in paginationItems" :key="`show-list-bottom-${pageItem.key}`" class="page-item" :class="{ active: pageItem.page === currentPage, disabled: pageItem.ellipsis }">
                              <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">{{ pageItem.label }}</span>
                              <button v-else class="page-link" :data-testid="`show-list-page-${pageItem.page}`" type="button" :aria-label="`Go to page ${pageItem.page}`" @click="goToPage(pageItem.page)">{{ pageItem.label }}</button>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage >= effectiveNumPages }">
                              <button class="page-link" data-testid="show-list-next" type="button" @click="goToPage(currentPage + 1)">Next</button>
                            </li>
                          </ul>
                          <CompactMobilePagination
                            :current-page="currentPage"
                            :total-pages="effectiveNumPages"
                            test-id-prefix="show-list"
                            @page-change="goToPage"
                          />
                        </nav>

                        <div
                          v-if="listEditMode && showListCanEdit"
                          class="film-list-detail__delete-row"
                        >
                          <button
                            class="btn-sm film-list-detail__delete-button"
                            data-testid="show-list-delete-button"
                            type="button"
                            :disabled="listDeletePending"
                            @click="deleteShowList"
                          >
                            Delete list
                          </button>
                        </div>
                      </div>

                      <div class="col-3 g-0 filters-section" data-testid="show-list-filters-sidebar">
                        <div class="container person-filters">
                          <div class="row g-0 filters-title-row">
                            <div class="col g-0">
                              <h3>Filters:</h3>
                            </div>
                          </div>

                          <template v-for="group in filterGroups" :key="`sidebar-${group.key}`">
                            <div class="row g-0 single-filter-title-row">
                              <div class="col g-0">
                                <h3>{{ group.title }}</h3>
                              </div>
                            </div>
                            <div class="row g-0 filter-row">
                              <div class="col g-0">
                                <a
                                  v-for="option in group.options"
                                  :key="option.value"
                                  :class="{ active: option.active }"
                                  href="#"
                                  @click.prevent="applyFilter(group.key, option.value)"
                                >
                                  {{ option.label }}
                                </a>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="overviewPath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="show-list"
            :target-id="listId"
            test-id-prefix="show-list"
          />
        </div>
        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="selectedRatingItem"
        class="person-compact-rating-picker-overlay"
        data-testid="show-list-rating-picker-overlay"
        @click.self="closeRatingPicker"
      >
        <div class="person-compact-rating-picker" data-testid="show-list-rating-picker">
          <button
            class="person-compact-rating-picker__close"
            data-testid="show-list-rating-picker-close"
            type="button"
            aria-label="Close rating picker"
            @click="closeRatingPicker"
          >
            <span
              class="person-compact-rating-picker__close-icon ion-close"
              aria-hidden="true"
            ></span>
          </button>

          <p class="person-compact-rating-picker__title">
            Rate {{ selectedRatingItem?.show?.title || 'show' }}
          </p>
          <div class="person-compact-rating-picker__stars">
            <button
              v-for="star in 10"
              :key="`film-list-rating-${selectedRatingItem?.show?.slug || 'show'}-${star}`"
              class="person-compact-rating-picker__star"
              data-testid="show-list-rating-picker-star"
              type="button"
              :disabled="selectedRatingItem ? showRatingPendingBySlug[selectedRatingItem.show.slug] : false"
              @mouseenter="previewRating(star)"
              @mouseleave="resetRatingPreview"
              @click="selectRating(star)"
            >
              <i :class="ratingPickerStarClass(star)"></i>
            </button>
          </div>
          <button
            class="person-compact-rating-picker__reset"
            data-testid="show-list-rating-picker-reset"
            type="button"
            :disabled="!selectedRatingItem || !selectedRatingItem.show.rating || showRatingPendingBySlug[selectedRatingItem.show.slug]"
            @click="resetSelectedItemRating"
          >
            Reset rating
          </button>
        </div>
      </div>
    </Teleport>
    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import CuratedListLabel from '../components/CuratedListLabel.vue'
import ResetListNumbers from '../components/ResetListNumbers.vue'
import { useListNumberReset } from '../utils/listNumberReset'
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CommentsPanel from '../components/CommentsPanel.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-person/person-detail.css'
import '../assets/single-film-list/styles.css'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { compactPaginationItems } from '../utils/pagination'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'

const route = useRoute()
const capturePageView = usePageViewTracking()
const router = useRouter()
const auth = useAuthStore()
auth.initFromStorage()

const logoPath = '/static/spa/branding/logo.png'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const pageSize = 50
const listFavouritesEnabled = false

const mobileSearchOpen = ref(false)
const filtersModalOpen = ref(false)
const showListLoading = ref(false)
const showListLoaded = ref(false)
const showListError = ref('')
const showList = ref({
  id: null,
  title: '',
  description: '',
  owner_name: '',
  is_owner: false,
  public: true,
  show_count: 0,
  page: 1,
  page_size: pageSize,
  num_pages: 1,
  server_paginated: false,
  filter_counts: {},
  updated_at: null,
  items: [],
})
const documentTitle = computed(() => buildDocumentTitle([showList.value.title, 'TV Show list']))
const selectedType = ref('all')
const selectedReleaseStatus = ref('all')
const selectedActivity = ref('all')
const sortMode = ref('number-asc')
const currentViewMode = ref('1')
const titleFilter = ref('')
const currentPage = ref(1)
const listContentStartRef = ref(null)
const ignoreFilterPageReset = ref(false)
const selectedRatingItem = ref(null)
const ratingPreview = ref(0)
const listEditMode = ref(false)
const reorderMode = ref(false)
const viewModeBeforeReordering = ref('1')
const editableListTitle = ref('')
const editableListDescription = ref('')
const editableListPublic = ref(true)
const editableItemDescriptions = ref({})
const savedEditableItems = ref([])
const pendingAddedItems = ref([])
const removedItemIds = ref([])
const addItemQuery = ref('')
const addItemSearchResults = ref([])
const addItemActiveIndex = ref(-1)
const addItemNotice = ref('')
const reorderedItemIds = ref([])
const savedReorderItemIds = ref([])
const reorderItemNumbers = ref({})
const savedReorderItemNumbers = ref({})
const reorderPageLengths = ref([])
const savedReorderPageLengths = ref([])
const reorderMoveNotice = ref({ text: '', position: '' })
const showUnsavedChangesNotice = ref(false)
const showSavedChangesNotice = ref(false)
const listDeletePending = ref(false)
let savedChangesNoticeTimeoutId = null
let reorderMoveNoticeTimeoutId = null
let addItemSearchRequestId = 0
const addItemResultRefs = []
const deleteListConfirmationMessage = "Delete this list? This will permanently delete the whole list and all its list items. This deletion can't be reverted."
const listDeletedNoticeStorageKey = 'filmsomniac:listDeletedNotice'

const showWatchlistPendingBySlug = ref({})
const showFavouritePendingBySlug = ref({})
const showRatingPendingBySlug = ref({})

function clearSavedChangesNoticeTimeout() {
  if (savedChangesNoticeTimeoutId !== null) {
    window.clearTimeout(savedChangesNoticeTimeoutId)
    savedChangesNoticeTimeoutId = null
  }
}

function dismissSavedChangesNotice() {
  clearSavedChangesNoticeTimeout()
  showSavedChangesNotice.value = false
}

function scheduleSavedChangesNoticeDismiss() {
  clearSavedChangesNoticeTimeout()
  savedChangesNoticeTimeoutId = window.setTimeout(() => {
    showSavedChangesNotice.value = false
    savedChangesNoticeTimeoutId = null
  }, 3000)
}

function clearReorderMoveNoticeTimeout() {
  if (reorderMoveNoticeTimeoutId !== null) {
    window.clearTimeout(reorderMoveNoticeTimeoutId)
    reorderMoveNoticeTimeoutId = null
  }
}

const authUsername = computed(() => auth.state.username || 'Account')
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const listSlugParam = computed(() => String(route.params.listSlug || ''))
const listId = computed(() => {
  const match = listSlugParam.value.match(/^(\d+)/)
  return match ? Number.parseInt(match[1], 10) : null
})
const activeSection = computed(() => (
  route.params.section === 'comments' ? 'comments' : 'overview'
))
const loginPath = computed(() => buildLoginPath(route.fullPath || overviewPath.value))
const slugifiedTitle = computed(() => slugifyText(showList.value.title || '') || 'list')
const overviewPath = computed(() => (
  listId.value ? `/lists/show/${listId.value}-${slugifiedTitle.value}` : '/lists/show'
))
const commentsPath = computed(() => `${overviewPath.value}/comments`)
const showTitleDisplayLimit = 80
const hasUnsavedChanges = computed(() => (
  editableListTitle.value !== (showList.value.title || '')
    || editableListDescription.value !== (showList.value.description || '')
    || editableListPublic.value !== Boolean(showList.value.public)
    || !editableItemsMatchSavedItems(showList.value.items, savedEditableItems.value)
    || removedItemIds.value.length > 0
))

const hasPendingItemDeletions = computed(() => removedItemIds.value.length > 0)

const allItems = computed(() => Array.isArray(showList.value.items) ? showList.value.items : [])
const showListOwnerPath = computed(() => buildFilmsomniacPath(showList.value.owner_name))

const showCountLabel = computed(() => {
  const count = showList.value.show_count || 0
  return `${count} ${count === 1 ? 'show' : 'shows'}`
})

const updatedAtLabel = computed(() => formatShortDate(showList.value.updated_at))

const sortButtons = computed(() => [
  { key: 'number-asc', label: 'Number 0-9', first: true, active: sortMode.value === 'number-asc' },
  { key: 'number-desc', label: 'Number 9-0', active: sortMode.value === 'number-desc' },
  { key: 'oldest', label: 'Oldest', active: sortMode.value === 'oldest' },
  { key: 'newest', label: 'Newest', active: sortMode.value === 'newest' },
  { key: 'title-asc', label: 'Title A-Z', active: sortMode.value === 'title-asc' },
  { key: 'title-desc', label: 'Title Z-A', active: sortMode.value === 'title-desc' },
])

const primarySortButtons = computed(() => sortButtons.value.slice(0, 4))

const secondarySortButtons = computed(() => [])

const titleSortButtons = computed(() => sortButtons.value.slice(4))

const viewModeButtons = computed(() => [
  {
    key: '1',
    label: 'Detailed view',
    first: true,
    active: currentViewMode.value === '1',
  },
  { key: '2', label: 'Poster grid view', active: currentViewMode.value === '2' },
  { key: '4', label: 'Text list view', active: currentViewMode.value === '4' },
])

function serverFilterCounts(groupKey) {
  if (!hasServerPagination.value) {
    return null
  }
  const counts = showList.value.filter_counts?.[groupKey]
  return counts && typeof counts === 'object' ? counts : null
}

function filterCount(counts, key, fallback) {
  const value = Number.parseInt(String(counts?.[key] ?? ''), 10)
  return Number.isFinite(value) ? value : fallback
}

const currentTypeCounts = computed(() => ({
  all: filterCount(serverFilterCounts('type'), 'all', allItems.value.length),
  tv_show: filterCount(
    serverFilterCounts('type'),
    'tv_show',
    countMatches(allItems.value, (item) => item.show.type === 'tv_show'),
  ),
  documentary: filterCount(
    serverFilterCounts('type'),
    'documentary',
    countMatches(allItems.value, (item) => item.show.type === 'documentary'),
  ),
  talk_show: filterCount(
    serverFilterCounts('type'),
    'talk_show',
    countMatches(allItems.value, (item) => item.show.type === 'talk_show'),
  ),
}))

const typeScopedItems = computed(() => {
  if (selectedType.value === 'all') {
    return allItems.value
  }
  return allItems.value.filter((item) => item.show.type === selectedType.value)
})

const currentReleaseStatusCounts = computed(() => ({
  all: filterCount(serverFilterCounts('release_status'), 'all', typeScopedItems.value.length),
  upcoming: filterCount(
    serverFilterCounts('release_status'),
    'upcoming',
    countMatches(typeScopedItems.value, (item) => item.show.release_status === 'upcoming'),
  ),
  ongoing: filterCount(
    serverFilterCounts('release_status'),
    'ongoing',
    countMatches(typeScopedItems.value, (item) => item.show.release_status === 'ongoing'),
  ),
  ended: filterCount(
    serverFilterCounts('release_status'),
    'ended',
    countMatches(typeScopedItems.value, (item) => item.show.release_status === 'ended'),
  ),
}))

const releaseStatusScopedItems = computed(() => {
  if (selectedReleaseStatus.value === 'all') {
    return typeScopedItems.value
  }
  return typeScopedItems.value.filter((item) => item.show.release_status === selectedReleaseStatus.value)
})

const activityCounts = computed(() => ({
  all: filterCount(serverFilterCounts('activity'), 'all', releaseStatusScopedItems.value.length),
  watchlisted: filterCount(
    serverFilterCounts('activity'),
    'watchlisted',
    countMatches(releaseStatusScopedItems.value, (item) => Boolean(item.show.watchlisted)),
  ),
  rated: filterCount(
    serverFilterCounts('activity'),
    'rated',
    countMatches(releaseStatusScopedItems.value, (item) => normalizeRatingValue(item.show.rating) !== null),
  ),
  favourited: filterCount(
    serverFilterCounts('activity'),
    'favourited',
    countMatches(releaseStatusScopedItems.value, (item) => Boolean(item.show.favourited)),
  ),
}))

function buildFilterOptions(allCount, selectedValue, definitions, options = {}) {
  const includeAllForSingleOption = Boolean(options.includeAllForSingleOption)
  const specificOptions = definitions
    .filter((definition) => definition.count > 0)
    .map((definition) => ({
      value: definition.value,
      label: `${definition.label} (${definition.count})`,
      active: selectedValue === definition.value,
    }))

  if (specificOptions.length === 0) {
    return [
      {
        value: 'all',
        label: `All (${allCount})`,
        active: selectedValue === 'all',
      },
    ]
  }

  if (specificOptions.length === 1 && !includeAllForSingleOption) {
    return specificOptions
  }

  return [
    {
      value: 'all',
      label: `All (${allCount})`,
      active: selectedValue === 'all',
    },
    ...specificOptions,
  ]
}

const typeFilterOptions = computed(() => (
  buildFilterOptions(currentTypeCounts.value.all, selectedType.value, [
    { value: 'tv_show', label: 'TV Show', count: currentTypeCounts.value.tv_show },
    { value: 'documentary', label: 'Documentary', count: currentTypeCounts.value.documentary },
    { value: 'talk_show', label: 'Talk show', count: currentTypeCounts.value.talk_show },
  ])
))

const releaseStatusFilterOptions = computed(() => (
  buildFilterOptions(currentReleaseStatusCounts.value.all, selectedReleaseStatus.value, [
    { value: 'upcoming', label: 'Upcoming', count: currentReleaseStatusCounts.value.upcoming },
    { value: 'ongoing', label: 'Ongoing', count: currentReleaseStatusCounts.value.ongoing },
    { value: 'ended', label: 'Ended', count: currentReleaseStatusCounts.value.ended },
  ], { includeAllForSingleOption: true })
))

const activityFilterOptions = computed(() => {
  if (!isAuthenticated.value) {
    return []
  }

  return buildFilterOptions(activityCounts.value.all, selectedActivity.value, [
    { value: 'watchlisted', label: 'Watchlisted', count: activityCounts.value.watchlisted },
    { value: 'rated', label: 'Rated', count: activityCounts.value.rated },
    { value: 'favourited', label: 'Favourited', count: activityCounts.value.favourited },
  ])
})

const activityScopedItems = computed(() => {
  if (hasServerPagination.value) {
    return releaseStatusScopedItems.value
  }
  if (!isAuthenticated.value || selectedActivity.value === 'all') {
    return releaseStatusScopedItems.value
  }

  return releaseStatusScopedItems.value.filter((item) => {
    switch (selectedActivity.value) {
      case 'watchlisted':
        return Boolean(item.show.watchlisted)
      case 'rated':
        return normalizeRatingValue(item.show.rating) !== null
      case 'favourited':
        return Boolean(item.show.favourited)
      default:
        return true
    }
  })
})

const normalizedTitleFilter = computed(() => titleFilter.value.trim().toLowerCase())

const filteredItems = computed(() => {
  if (normalizedTitleFilter.value.length < 2) {
    return activityScopedItems.value
  }

  return activityScopedItems.value.filter((item) => {
    const title = String(item.show.title || '').toLowerCase()
    const startYear = String(item.show.start_year || '').toLowerCase()
    const yearLabel = String(item.show.year || '').toLowerCase()
    return (
      title.includes(normalizedTitleFilter.value)
      || startYear.includes(normalizedTitleFilter.value)
      || yearLabel.includes(normalizedTitleFilter.value)
    )
  })
})

const sortedItems = computed(() => {
  const items = [...filteredItems.value]

  switch (sortMode.value) {
    case 'number-desc':
      return items.sort((left, right) => numericCompare(right.number, left.number, right.show.title, left.show.title))
    case 'oldest':
      return items.sort((left, right) => numericCompare(showStartYearValue(left), showStartYearValue(right), left.show.title, right.show.title))
    case 'newest':
      return items.sort((left, right) => numericCompare(showStartYearValue(right), showStartYearValue(left), right.show.title, left.show.title))
    case 'title-asc':
      return items.sort((left, right) => compareStrings(left.show.title, right.show.title))
    case 'title-desc':
      return items.sort((left, right) => compareStrings(right.show.title, left.show.title))
    case 'number-asc':
    default:
      return items.sort((left, right) => numericCompare(left.number, right.number, left.show.title, right.show.title))
  }
})

const hasServerPagination = computed(() => (
  showList.value.server_paginated
    && Number.parseInt(showList.value.page_size, 10) === pageSize
    && Number.parseInt(showList.value.num_pages, 10) > 0
))

const numPages = computed(() => {
  if (hasServerPagination.value) {
    return Math.max(1, Number.parseInt(showList.value.num_pages, 10) || 1)
  }
  return Math.max(1, Math.ceil(sortedItems.value.length / pageSize))
})

const paginatedItems = computed(() => {
  if (hasServerPagination.value) {
    return sortedItems.value
  }
  const startIndex = (currentPage.value - 1) * pageSize
  return sortedItems.value.slice(startIndex, startIndex + pageSize)
})

const reorderOrderedItems = computed(() => {
  if (!reorderMode.value) {
    return sortedItems.value
  }

  const itemsById = new Map(sortedItems.value.map((item) => [item.id, item]))
  const orderedIds = reorderedItemIds.value.filter((id) => itemsById.has(id))
  const missingIds = sortedItems.value
    .map((item) => item.id)
    .filter((id) => !orderedIds.includes(id))

  return [...orderedIds, ...missingIds]
    .map((id) => itemsById.get(id))
    .filter(Boolean)
})

const reorderNumPages = computed(() => (
  Math.max(1, reorderPageLengths.value.length || 1)
))

const effectiveNumPages = computed(() => (
  reorderMode.value ? reorderNumPages.value : numPages.value
))
const paginationItems = computed(() => compactPaginationItems(currentPage.value, effectiveNumPages.value))

const reorderPaginatedItems = computed(() => {
  const startIndex = reorderPageStartIndex(currentPage.value)
  const currentLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  return reorderOrderedItems.value.slice(startIndex, startIndex + currentLength)
})

const hasReorderChanges = computed(() => {
  if (!reorderMode.value) {
    return false
  }

  if (reorderedItemIds.value.length !== savedReorderItemIds.value.length) {
    return true
  }

  if (reorderPageLengths.value.length !== savedReorderPageLengths.value.length) {
    return true
  }

  if (reorderPageLengths.value.some((length, index) => length !== savedReorderPageLengths.value[index])) {
    return true
  }

  if (reorderedItemIds.value.some((id, index) => id !== savedReorderItemIds.value[index])) {
    return true
  }

  return reorderedItemIds.value.some((id) => (
    reorderItemNumbers.value[id] !== savedReorderItemNumbers.value[id]
  ))
})

const noItemsMessage = computed(() => (
  activityScopedItems.value.length === 0
    ? 'No shows on this list.'
    : 'No shows found.'
))

const showNoResults = computed(() => (
  activityScopedItems.value.length > 0 && filteredItems.value.length === 0
))

const filterGroups = computed(() => {
  const groups = [
    {
      key: 'type',
      title: 'Type:',
      options: typeFilterOptions.value,
    },
    {
      key: 'release-status',
      title: 'Status:',
      options: releaseStatusFilterOptions.value,
    },
  ]

  if (isAuthenticated.value) {
    groups.push({
      key: 'activity',
      title: 'Your activity',
      options: activityFilterOptions.value,
    })
  }

  return groups
})
const filterModalRows = computed(() => {
  const groups = filterGroups.value
  const rows = []

  for (let index = 0; index < groups.length; index += 2) {
    const rowGroups = groups.slice(index, index + 2)
    rows.push({
      key: rowGroups.map((group) => group.key).join('-'),
      groups: rowGroups,
    })
  }

  return rows
})
const filterButtonLabel = computed(() => {
  return 'Filters'
})

function pageFromRoute() {
  const rawPage = Array.isArray(route.query.page) ? route.query.page[0] : route.query.page
  const page = Number.parseInt(String(rawPage || ''), 10)
  return Number.isFinite(page) && page > 0 ? page : 1
}

function normalizedPage(pageNumber) {
  return Math.min(Math.max(pageNumber, 1), effectiveNumPages.value)
}

function queryForPage(pageNumber) {
  const query = { ...route.query }
  if (pageNumber > 1) {
    query.page = String(pageNumber)
  } else {
    delete query.page
  }
  return query
}

function syncPageRoute(pageNumber, { replace = true } = {}) {
  const location = { path: route.path, query: queryForPage(pageNumber) }
  return replace ? router.replace(location) : router.push(location)
}

function resetCurrentPage() {
  currentPage.value = 1
  void syncPageRoute(1)
}

async function scrollToListContentStart() {
  await nextTick()
  if (typeof listContentStartRef.value?.scrollIntoView === 'function') {
    listContentStartRef.value.scrollIntoView({ behavior: 'auto', block: 'start' })
  }
}

watch([selectedType, selectedReleaseStatus, selectedActivity], () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!showListLoaded.value) {
    return
  }
  resetCurrentPage()
  if (hasServerPagination.value && !listEditMode.value && !reorderMode.value) {
    void loadShowListDetail(listId.value, { page: 1, preserveFilters: true })
  }
})

watch([sortMode, titleFilter], () => {
  if (ignoreFilterPageReset.value) {
    return
  }
  if (!showListLoaded.value) {
    return
  }
  if (!hasServerPagination.value) {
    resetCurrentPage()
  }
})

watch(typeFilterOptions, (options) => {
  if (!options.some((option) => option.value === selectedType.value)) {
    selectedType.value = options[0]?.value || 'all'
  }
}, { immediate: true })

watch(releaseStatusFilterOptions, (options) => {
  if (!options.some((option) => option.value === selectedReleaseStatus.value)) {
    selectedReleaseStatus.value = options[0]?.value || 'all'
  }
}, { immediate: true })

watch(activityFilterOptions, (options) => {
  if (!isAuthenticated.value) {
    selectedActivity.value = 'all'
    return
  }
  if (!options.some((option) => option.value === selectedActivity.value)) {
    selectedActivity.value = options[0]?.value || 'all'
  }
}, { immediate: true })

watch([editableListTitle, editableListDescription, editableItemDescriptions, savedEditableItems, removedItemIds], () => {
  if (listEditMode.value && hasUnsavedChanges.value) {
    dismissSavedChangesNotice()
  }
}, { deep: true })

onBeforeUnmount(() => {
  clearSavedChangesNoticeTimeout()
  clearReorderMoveNoticeTimeout()
})

watch(numPages, (value) => {
  if (currentPage.value > value) {
    currentPage.value = value
    void syncPageRoute(value)
  }
})

watch(() => route.query.page, async () => {
  currentPage.value = normalizedPage(pageFromRoute())
  if (
    listId.value
      && showListLoaded.value
      && hasServerPagination.value
      && !reorderMode.value
  ) {
    await loadShowListDetail(listId.value, {
      preserveFilters: true,
      preserveEditMode: listEditMode.value,
    })
  }
})

watch([listId, isAuthenticated], async ([currentListId]) => {
  if (!currentListId) {
    showList.value = emptyShowList()
    showListLoaded.value = true
    showListError.value = 'This list could not be found.'
    return
  }
  await loadShowListDetail(currentListId)
}, { immediate: true })

watch(documentTitle, setDocumentTitle, { immediate: true })

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function buildAuthHeaders() {
  const headers = {}
  if (auth.state.access) {
    headers.Authorization = `Bearer ${auth.state.access}`
  }
  return headers
}

function emptyShowList() {
  return {
    id: null,
    title: '',
    description: '',
    owner_name: '',
    is_owner: false,
    show_count: 0,
    page: 1,
    page_size: pageSize,
    num_pages: 1,
    server_paginated: false,
    filter_counts: {},
    updated_at: null,
    items: [],
  }
}

function buildJsonAuthHeaders() {
  return {
    ...buildAuthHeaders(),
    'Content-Type': 'application/json',
  }
}

function buildEditableItemDescriptions(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item) => [item.id, item.description || '']),
  )
}

function resetRemovedItemIds() {
  removedItemIds.value = []
}

function isListItemPendingRemoval(itemId) {
  return removedItemIds.value.includes(itemId)
}

function cloneShowListItems(items) {
  return (Array.isArray(items) ? items : []).map((item) => ({
    ...item,
    show: item?.show ? { ...item.show } : item.show,
  }))
}

function editableItemsMatchSavedItems(currentItems, savedItems) {
  if (currentItems.length !== savedItems.length) {
    return false
  }

  return currentItems.every((item, index) => {
    const savedItem = savedItems[index]
    return savedItem
      && item.id === savedItem.id
      && (editableItemDescriptions.value[item.id] ?? '') === (savedItem.description || '')
  })
}

function buildReorderedItemIds(items) {
  return (Array.isArray(items) ? items : []).map((item) => item.id)
}

function buildReorderItemNumbers(items) {
  return Object.fromEntries(
    (Array.isArray(items) ? items : []).map((item, index) => [
      item.id,
      Number.isFinite(Number.parseInt(item.number, 10)) ? Number.parseInt(item.number, 10) : index + 1,
    ]),
  )
}

function reorderItemNumber(item) {
  if (!item) {
    return 0
  }
  return reorderItemNumbers.value[item.id] ?? item.number ?? 0
}

function resetAddItemSearch() {
  addItemSearchRequestId += 1
  addItemQuery.value = ''
  addItemSearchResults.value = []
  addItemActiveIndex.value = -1
  addItemResultRefs.length = 0
}

function resetPendingAddedItems() {
  pendingAddedItems.value = []
}

function isShowAlreadyOnList(showId) {
  return showList.value.items.some((item) => item?.show?.id === showId)
}

function isShowPendingAdd(showId) {
  return pendingAddedItems.value.some((item) => item?.show?.id === showId)
}

function isShowAddItemDisabled(showId) {
  return isShowAlreadyOnList(showId) || isShowPendingAdd(showId)
}

async function handleAddItemSearchInput() {
  if (!listEditMode.value || !showListCanEdit.value || !listId.value) {
    resetAddItemSearch()
    return
  }

  const query = addItemQuery.value.trim()
  const requestId = ++addItemSearchRequestId

  if (query.length < 2) {
    addItemSearchResults.value = []
    addItemActiveIndex.value = -1
    addItemResultRefs.length = 0
    return
  }

  try {
    const response = await fetch(`/api/v1/shows/lists/${listId.value}/search?q=${encodeURIComponent(query)}`, {
      headers: buildAuthHeaders(),
    })

    if (requestId !== addItemSearchRequestId) {
      return
    }

    if (!response.ok) {
      addItemSearchResults.value = []
      return
    }

    const data = await response.json()
    addItemSearchResults.value = Array.isArray(data.results) ? data.results : []
    addItemActiveIndex.value = -1
    addItemResultRefs.length = 0
  } catch {
    if (requestId === addItemSearchRequestId) {
      addItemSearchResults.value = []
      addItemActiveIndex.value = -1
      addItemResultRefs.length = 0
    }
  }
}

function setAddItemResultRef(element, index) {
  if (!element) {
    return
  }
  addItemResultRefs[index] = element
}

async function scrollActiveAddItemResultIntoView() {
  await nextTick()
  const activeElement = addItemResultRefs[addItemActiveIndex.value]
  if (typeof activeElement?.scrollIntoView === 'function') {
    activeElement.scrollIntoView({ block: 'nearest' })
  }
}

function setAddItemActiveIndex(index) {
  addItemActiveIndex.value = index
  scrollActiveAddItemResultIntoView()
}

function isAddItemSearchResultSelectable(result) {
  return Boolean(result?.id) && !isShowAddItemDisabled(result.id)
}

function setSelectableAddItemActiveIndex(result, index) {
  if (isAddItemSearchResultSelectable(result)) {
    setAddItemActiveIndex(index)
  }
}

function getNextSelectableAddItemIndex(direction) {
  const results = addItemSearchResults.value
  if (results.length === 0) {
    return -1
  }

  const startIndex = addItemActiveIndex.value < 0
    ? (direction > 0 ? -1 : results.length)
    : addItemActiveIndex.value

  for (let offset = 1; offset <= results.length; offset += 1) {
    const index = (startIndex + (direction * offset) + results.length) % results.length
    if (isAddItemSearchResultSelectable(results[index])) {
      return index
    }
  }

  return -1
}

function highlightNextAddItemResult() {
  const nextIndex = getNextSelectableAddItemIndex(1)
  if (nextIndex >= 0) {
    setAddItemActiveIndex(nextIndex)
  }
}

function highlightPreviousAddItemResult() {
  const previousIndex = getNextSelectableAddItemIndex(-1)
  if (previousIndex >= 0) {
    setAddItemActiveIndex(previousIndex)
  }
}

function handleAddItemEnter() {
  if (addItemActiveIndex.value < 0) {
    return
  }
  addSearchResult(addItemSearchResults.value[addItemActiveIndex.value])
}

async function addSearchResult(result) {
  if (!result?.id || isShowAddItemDisabled(result.id)) {
    return
  }

  pendingAddedItems.value = [
    ...pendingAddedItems.value,
    {
      number: showList.value.items.length + 1,
      description: '',
      show: {
        id: result.id,
        title: result.title || '',
        slug: result.slug || '',
        url: result.url || '#',
        year: result.year || '',
        start_year: result.year || '',
        end_year: null,
        poster_url: result.poster_url || null,
        type: result.type || 'tv_show',
        release_status: result.release_status || 'ongoing',
        watchlisted: false,
        favourited: false,
        rating: null,
      },
    },
  ]
  const nextNumber = showList.value.items.length + 1

  try {
    const response = await fetch(`/api/v1/shows/lists/${listId.value}/`, {
      method: 'PATCH',
      headers: buildJsonAuthHeaders(),
      body: JSON.stringify({
        items: [
          ...showList.value.items.map((item) => ({
            id: item.id,
            description: item.description || '',
          })),
          { show_id: result.id, description: '', number: nextNumber },
        ],
      }),
    })

    if (!response.ok) {
      return
    }

    const data = await response.json()
    const fallbackNewItem = {
      id: `new-${result.id}`,
      number: nextNumber,
      description: '',
      added_at: null,
      updated_at: null,
      show: {
        id: result.id,
        title: result.title || '',
        slug: result.slug || '',
        url: result.url || '#',
        year: result.year || '',
        start_year: result.year || '',
        end_year: null,
        poster_url: result.poster_url || null,
        type: result.type || 'tv_show',
        release_status: result.release_status || 'ongoing',
        watchlisted: false,
        favourited: false,
        rating: null,
      },
    }
    const nextItems = Array.isArray(data.items) ? data.items : [...showList.value.items, fallbackNewItem]

    showList.value = {
      ...showList.value,
      ...data,
      title: data.title ?? showList.value.title,
      description: data.description ?? showList.value.description,
      show_count: data.show_count ?? nextItems.length,
      items: nextItems,
    }

    const nextEditableItemDescriptions = { ...editableItemDescriptions.value }
    nextItems.forEach((item) => {
      if (!(item.id in nextEditableItemDescriptions)) {
        nextEditableItemDescriptions[item.id] = item.description || ''
      }
    })
    editableItemDescriptions.value = nextEditableItemDescriptions
    savedEditableItems.value = cloneShowListItems(showList.value.items)
    addItemNotice.value = `'${result.title || 'Item'}' added to the list as #${nextNumber}.`
  } finally {
    pendingAddedItems.value = pendingAddedItems.value.filter((item) => item?.show?.id !== result.id)
    resetAddItemSearch()
  }
}

function removeListItemFromEditing(itemId) {
  if (isListItemPendingRemoval(itemId)) {
    return
  }
  removedItemIds.value = [...removedItemIds.value, itemId]
  dismissSavedChangesNotice()
}

function undoListItemRemoval(itemId) {
  removedItemIds.value = removedItemIds.value.filter((id) => id !== itemId)
  dismissSavedChangesNotice()
}

function buildReorderPageLengths(itemCount) {
  const lengths = []
  let remaining = Math.max(0, itemCount)

  while (remaining > 0) {
    const nextLength = Math.min(pageSize, remaining)
    lengths.push(nextLength)
    remaining -= nextLength
  }

  return lengths.length > 0 ? lengths : [0]
}

function reorderPageStartIndex(pageNumber) {
  const boundedPageNumber = Math.max(1, pageNumber)
  return reorderPageLengths.value
    .slice(0, boundedPageNumber - 1)
    .reduce((sum, length) => sum + length, 0)
}

function reorderDisplayNumber(localIndex) {
  return reorderItemNumber(reorderPaginatedItems.value[localIndex])
}

function slugifyText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function truncatedShowTitle(value) {
  const normalized = String(value || '').trim()

  if (normalized.length <= showTitleDisplayLimit) {
    return normalized
  }

  const truncated = normalized.slice(0, showTitleDisplayLimit).trim()
  const lastSpaceIndex = truncated.lastIndexOf(' ')

  if (lastSpaceIndex <= 0) {
    return `${truncated}...`
  }

  return `${truncated.slice(0, lastSpaceIndex).trim()}...`
}

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}

function formatShortDate(value) {
  if (!value) {
    return ''
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed)
}

function normalizeRatingValue(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const normalized = Number.parseInt(String(value), 10)
  if (Number.isNaN(normalized) || normalized < 1 || normalized > 10) {
    return null
  }
  return normalized
}

function countMatches(items, matcher) {
  return items.reduce((total, item) => (matcher(item) ? total + 1 : total), 0)
}

function compareStrings(left, right) {
  return String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' })
}

function numericCompare(leftValue, rightValue, leftTitle, rightTitle) {
  const leftNumber = Number.parseInt(String(leftValue || 0), 10) || 0
  const rightNumber = Number.parseInt(String(rightValue || 0), 10) || 0
  if (leftNumber !== rightNumber) {
    return leftNumber - rightNumber
  }
  return compareStrings(leftTitle, rightTitle)
}

function showStartYearValue(item) {
  return Number.parseInt(String(item?.show?.start_year || ''), 10) || 0
}

function defaultTypeValue(items) {
  const tvShowCount = countMatches(items, (item) => item.show.type === 'tv_show')
  if (tvShowCount > 0) {
    return 'tv_show'
  }
  const documentaryCount = countMatches(items, (item) => item.show.type === 'documentary')
  if (documentaryCount > 0) {
    return 'documentary'
  }
  const talkShowCount = countMatches(items, (item) => item.show.type === 'talk_show')
  if (talkShowCount > 0) {
    return 'talk_show'
  }
  return 'all'
}

function initializeFilters(items) {
  ignoreFilterPageReset.value = true
  void nextTick(() => {
    ignoreFilterPageReset.value = false
  })
  const nextType = defaultTypeValue(items)
  selectedType.value = nextType
  selectedReleaseStatus.value = 'all'
  selectedActivity.value = 'all'
  sortMode.value = 'number-asc'
  currentViewMode.value = '1'
  viewModeBeforeReordering.value = '1'
  titleFilter.value = ''
  currentPage.value = normalizedPage(pageFromRoute())
  filtersModalOpen.value = false
}

function resetFiltersForNormalListMode() {
  selectedType.value = 'all'
  selectedReleaseStatus.value = 'all'
  selectedActivity.value = 'all'
  titleFilter.value = ''
  resetCurrentPage()
  filtersModalOpen.value = false
}

function showListDetailQuery(pageNumber, { sort = sortMode.value } = {}) {
  const params = new URLSearchParams()
  if (pageNumber > 1) {
    params.set('page', String(pageNumber))
  }
  if (selectedType.value !== 'all') {
    params.set('type', selectedType.value)
  }
  if (selectedReleaseStatus.value !== 'all') {
    params.set('release_status', selectedReleaseStatus.value)
  }
  if (isAuthenticated.value && selectedActivity.value !== 'all') {
    params.set('activity', selectedActivity.value)
  }
  if (sort !== 'number-asc') {
    params.set('sort', sort)
  }
  const queryString = params.toString()
  return queryString ? `?${queryString}` : ''
}

async function loadShowListDetail(currentListId, {
  page = null,
  preserveFilters = false,
  preserveEditMode = false,
  sort = sortMode.value,
} = {}) {
  const confirmPageView = capturePageView()
  const shouldPreserveEditMode = preserveEditMode && listEditMode.value
  showListLoading.value = true
  showListLoaded.value = false
  showListError.value = ''
  selectedRatingItem.value = null
  ratingPreview.value = 0
  if (!shouldPreserveEditMode) {
    listEditMode.value = false
  }
  reorderMode.value = false
  editableListTitle.value = ''
  editableListDescription.value = ''
  editableListPublic.value = true
  editableItemDescriptions.value = {}
  savedEditableItems.value = []
  resetPendingAddedItems()
  resetRemovedItemIds()
  addItemNotice.value = ''
  resetAddItemSearch()
  reorderedItemIds.value = []
  savedReorderItemIds.value = []
  reorderItemNumbers.value = {}
  savedReorderItemNumbers.value = {}
  reorderPageLengths.value = []
  savedReorderPageLengths.value = []
  clearReorderMoveNotice()
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()

  try {
    const requestedPage = page ?? pageFromRoute()
    const detailUrl = `/api/v1/shows/lists/${currentListId}/${showListDetailQuery(requestedPage, { sort })}`
    const response = await fetch(detailUrl, {
      headers: buildAuthHeaders(),
    })

    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      showListError.value = 'This list could not be found.'
      showList.value = emptyShowList()
      showListLoaded.value = true
      return
    }

    if (response.status === 404) {
      showListError.value = 'This list could not be found.'
      showList.value = emptyShowList()
      showListLoaded.value = true
      return
    }

    if (!response.ok) {
      showListError.value = 'The list could not be loaded.'
      showList.value = emptyShowList()
      showListLoaded.value = true
      return
    }

    const data = await response.json()
    if (data.redirect_url) {
      window.location.assign(data.redirect_url)
      return
    }
    confirmPageView()
    showList.value = {
      ...emptyShowList(),
      ...data,
      server_paginated: Object.prototype.hasOwnProperty.call(data, 'num_pages'),
      items: Array.isArray(data.items) ? data.items : [],
    }
    currentPage.value = normalizedPage(Number.parseInt(showList.value.page, 10) || requestedPage)
    savedEditableItems.value = cloneShowListItems(showList.value.items)
    if (shouldPreserveEditMode) {
      editableListTitle.value = showList.value.title || ''
      editableListDescription.value = showList.value.description || ''
      editableListPublic.value = Boolean(showList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(showList.value.items)
      listEditMode.value = true
    }
    if (!preserveFilters) {
      initializeFilters(showList.value.items)
    }
    showListLoaded.value = true
  } catch {
    showListError.value = 'The list could not be loaded.'
    showList.value = emptyShowList()
    showListLoaded.value = true
  } finally {
    showListLoading.value = false
  }
}

async function setSortMode(mode) {
  if (hasServerPagination.value && listId.value) {
    resetCurrentPage()
    await loadShowListDetail(listId.value, { page: 1, preserveFilters: true, sort: mode })
  }
  sortMode.value = mode
}

function setViewMode(mode) {
  currentViewMode.value = mode
}

function toggleFilters() {
  filtersModalOpen.value = !filtersModalOpen.value
}

function applyFilter(groupKey, value) {
  switch (groupKey) {
    case 'type':
      selectedType.value = value
      break
    case 'release-status':
      selectedReleaseStatus.value = value
      break
    case 'activity':
      selectedActivity.value = value
      break
    default:
      break
  }

}

async function goToPage(pageNumber) {
  if (pageNumber < 1 || pageNumber > effectiveNumPages.value) {
    return
  }
  if (listEditMode.value && hasUnsavedChanges.value) {
    showUnsavedChangesNotice.value = true
    dismissSavedChangesNotice()
    return
  }
  clearReorderMoveNotice()
  await syncPageRoute(pageNumber, { replace: false })
  currentPage.value = pageNumber
  await scrollToListContentStart()
}

async function startListEditing() {
  if (!showListCanEdit.value) {
    return
  }

  if (listEditMode.value) {
    if (!hasUnsavedChanges.value) {
      editableListTitle.value = showList.value.title || ''
      editableListDescription.value = showList.value.description || ''
      editableListPublic.value = Boolean(showList.value.public)
      editableItemDescriptions.value = buildEditableItemDescriptions(showList.value.items)
      savedEditableItems.value = cloneShowListItems(showList.value.items)
      resetPendingAddedItems()
      resetRemovedItemIds()
      resetFiltersForNormalListMode()
      listEditMode.value = false
      showUnsavedChangesNotice.value = false
      addItemNotice.value = ''
      resetAddItemSearch()
      dismissSavedChangesNotice()
    } else {
      showUnsavedChangesNotice.value = true
      dismissSavedChangesNotice()
    }
    return
  }

  await loadAllReorderItems()
  editableListTitle.value = showList.value.title || ''
  editableListDescription.value = showList.value.description || ''
  editableListPublic.value = Boolean(showList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(showList.value.items)
  savedEditableItems.value = cloneShowListItems(showList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  currentViewMode.value = '1'
  reorderMode.value = false
  listEditMode.value = true
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  dismissSavedChangesNotice()
}

const showListCanEdit = computed(() => Boolean(
  showList.value.can_edit ?? showList.value.is_owner,
))

const { resetNumbersPending, resetNumbersNotice, resetNumbers } = useListNumberReset(
  showList,
  buildAuthHeaders,
  () => loadShowListDetail(listId.value, { page: currentPage.value, preserveFilters: true }),
)

async function startReordering() {
  if (!showListCanEdit.value) {
    return
  }

  titleFilter.value = ''
  await loadAllReorderItems()
  viewModeBeforeReordering.value = currentViewMode.value
  reorderedItemIds.value = buildReorderedItemIds(sortedItems.value)
  savedReorderItemIds.value = buildReorderedItemIds(sortedItems.value)
  reorderItemNumbers.value = buildReorderItemNumbers(sortedItems.value)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(sortedItems.value.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
  resetCurrentPage()
  currentViewMode.value = '4'
  listEditMode.value = false
  reorderMode.value = true
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

async function loadAllReorderItems() {
  if (!hasServerPagination.value || numPages.value <= 1 || !listId.value) {
    return
  }

  const pageCount = numPages.value
  const pageItems = new Map()

  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    if (pageNumber === currentPage.value) {
      pageItems.set(pageNumber, showList.value.items)
      continue
    }

    const response = await fetch(`/api/v1/shows/lists/${listId.value}/${showListDetailQuery(pageNumber)}`, {
      headers: buildAuthHeaders(),
    })

    if (!response.ok) {
      return
    }

    const data = await response.json()
    pageItems.set(pageNumber, Array.isArray(data.items) ? data.items : [])
  }

  const combinedItems = []
  for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
    combinedItems.push(...(pageItems.get(pageNumber) || []))
  }

  showList.value = {
    ...showList.value,
    show_count: combinedItems.length,
    items: combinedItems,
    page: 1,
    num_pages: Math.max(1, Math.ceil(combinedItems.length / pageSize)),
    server_paginated: false,
  }
}

function finishReordering() {
  if (!reorderMode.value) {
    return
  }

  if (!hasReorderChanges.value) {
    currentViewMode.value = viewModeBeforeReordering.value || '1'
    resetCurrentPage()
    reorderMode.value = false
    showUnsavedChangesNotice.value = false
    dismissSavedChangesNotice()
    return
  }

  showUnsavedChangesNotice.value = true

  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
  currentViewMode.value = viewModeBeforeReordering.value || '1'
  resetCurrentPage()
  reorderMode.value = false
  showUnsavedChangesNotice.value = false
  dismissSavedChangesNotice()
}

function resetReorderChanges() {
  reorderedItemIds.value = [...savedReorderItemIds.value]
  reorderItemNumbers.value = { ...savedReorderItemNumbers.value }
  reorderPageLengths.value = [...savedReorderPageLengths.value]
  clearReorderMoveNotice()
}

function discardReorderChanges() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  resetReorderChanges()
}

function reorderItemIndex(itemId) {
  return reorderOrderedItems.value.findIndex((item) => item.id === itemId)
}

function isFirstReorderItem(itemId) {
  return reorderItemIndex(itemId) === 0
}

function isLastReorderItem(itemId) {
  const itemIndex = reorderItemIndex(itemId)
  return itemIndex === reorderOrderedItems.value.length - 1
}

function clearReorderMoveNotice() {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text: '', position: '' }
}

function showReorderMoveNotice(text, position) {
  clearReorderMoveNoticeTimeout()
  reorderMoveNotice.value = { text, position }
  reorderMoveNoticeTimeoutId = window.setTimeout(() => {
    reorderMoveNotice.value = { text: '', position: '' }
    reorderMoveNoticeTimeoutId = null
  }, 5000)
}

function showReorderItemTitle(item) {
  return item?.show?.title || 'List item'
}

function moveReorderItem(itemId, direction) {
  if (!reorderMode.value || !direction) {
    return
  }

  const nextIds = [...reorderedItemIds.value]
  const currentIndex = nextIds.indexOf(itemId)
  const targetIndex = currentIndex + direction

  if (currentIndex === -1 || targetIndex < 0 || targetIndex >= nextIds.length) {
    return
  }

  const currentPageStartIndex = reorderPageStartIndex(currentPage.value)
  const currentPageLength = reorderPageLengths.value[currentPage.value - 1] || pageSize
  const currentPageEndIndex = currentPageStartIndex + currentPageLength - 1
  const movedItem = reorderOrderedItems.value[currentIndex]
  const movedTitle = showReorderItemTitle(movedItem)

  const targetId = nextIds[targetIndex]
  const targetItem = reorderOrderedItems.value[targetIndex]
  nextIds[targetIndex] = itemId
  nextIds[currentIndex] = targetId

  reorderedItemIds.value = nextIds
  reorderItemNumbers.value = {
    ...reorderItemNumbers.value,
    [itemId]: reorderItemNumber(targetItem),
    [targetId]: reorderItemNumber(movedItem),
  }

  if (direction > 0 && currentIndex === currentPageEndIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to next page to place #${targetIndex + 1}.`, 'below')
  } else if (direction < 0 && currentIndex === currentPageStartIndex) {
    showReorderMoveNotice(`${movedTitle} was moved to previous page to place #${targetIndex + 1}.`, 'above')
  } else {
    clearReorderMoveNotice()
  }
}
function discardListEditing() {
  const shouldDiscard = window.confirm('Discard all unsaved changes?')
  if (!shouldDiscard) {
    return
  }

  showList.value = {
    ...showList.value,
    show_count: savedEditableItems.value.length,
    items: cloneShowListItems(savedEditableItems.value),
  }
  editableListDescription.value = showList.value.description || ''
  editableListTitle.value = showList.value.title || ''
  editableListPublic.value = Boolean(showList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(showList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  dismissSavedChangesNotice()
}

async function saveListEditing() {
  if (!showListCanEdit.value || !listId.value) {
    return
  }

  if (hasPendingItemDeletions.value) {
    const shouldDelete = window.confirm('There are items to be deleted. Save changes and delete them?')
    if (!shouldDelete) {
      return
    }
  }

  const response = await fetch(`/api/v1/shows/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      title: editableListTitle.value,
      description: editableListDescription.value,
      public: editableListPublic.value,
      removed_item_ids: removedItemIds.value,
      items: showList.value.items
        .filter((item) => !isListItemPendingRemoval(item.id))
        .map((item) => ({
          id: item.id,
          description: editableItemDescriptions.value[item.id] ?? '',
        })),
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const nextItems = Array.isArray(data.items)
    ? data.items
    : showList.value.items.map((item) => ({
        ...item,
        description: editableItemDescriptions.value[item.id] ?? '',
      }))
  showList.value = {
    ...showList.value,
    ...data,
    title: data.title ?? '',
    description: data.description ?? '',
    show_count: data.show_count ?? nextItems.length,
    items: nextItems,
  }
  editableListTitle.value = showList.value.title || ''
  editableListDescription.value = showList.value.description || ''
  editableListPublic.value = Boolean(showList.value.public)
  editableItemDescriptions.value = buildEditableItemDescriptions(showList.value.items)
  savedEditableItems.value = cloneShowListItems(showList.value.items)
  resetPendingAddedItems()
  resetRemovedItemIds()
  showUnsavedChangesNotice.value = false
  addItemNotice.value = ''
  resetAddItemSearch()
  showSavedChangesNotice.value = true
  scheduleSavedChangesNoticeDismiss()
}

async function deleteShowList() {
  if (!showListCanEdit.value || !listId.value || listDeletePending.value) {
    return
  }

  const shouldDelete = window.confirm(deleteListConfirmationMessage)
  if (!shouldDelete) {
    return
  }

  listDeletePending.value = true
  try {
    const response = await fetch(`/api/v1/shows/lists/${listId.value}/`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })

    if (response.ok) {
      storeListDeletedNotice('Show', showList.value.title)
      await router.push('/home/lists')
    }
  } finally {
    listDeletePending.value = false
  }
}

function storeListDeletedNotice(listMediumType, listTitle) {
  try {
    window.sessionStorage.setItem(
      listDeletedNoticeStorageKey,
      JSON.stringify({
        message: `${listMediumType} list '${String(listTitle || '').trim() || 'Untitled'}' was deleted.`,
      }),
    )
  } catch {
    // The redirect still works if browser storage is unavailable.
  }
}

async function saveReordering() {
  if (!showListCanEdit.value || !listId.value) {
    return
  }

  const reorderedItems = reorderOrderedItems.value
    .map((item) => ({
      id: item.id,
      description: item.description || '',
      number: reorderItemNumber(item),
    }))
    .filter((item) => item.number !== savedReorderItemNumbers.value[item.id])

  const response = await fetch(`/api/v1/shows/lists/${listId.value}/`, {
    method: 'PATCH',
    headers: buildJsonAuthHeaders(),
    body: JSON.stringify({
      description: showList.value.description || '',
      removed_item_ids: [],
      items: reorderedItems,
    }),
  })

  if (!response.ok) {
    return
  }

  const data = await response.json()
  const reorderedItemsById = new Map(reorderedItems.map((item) => [item.id, item]))
  const nextItems = reorderOrderedItems.value.map((item) => ({
    ...item,
    description: reorderedItemsById.get(item.id)?.description ?? item.description ?? '',
    number: reorderItemNumber(item),
  }))

  showList.value = {
    ...showList.value,
    ...data,
    description: data.description ?? '',
    items: nextItems,
  }
  reorderedItemIds.value = buildReorderedItemIds(showList.value.items)
  savedReorderItemIds.value = [...reorderedItemIds.value]
  reorderItemNumbers.value = buildReorderItemNumbers(showList.value.items)
  savedReorderItemNumbers.value = { ...reorderItemNumbers.value }
  reorderPageLengths.value = buildReorderPageLengths(showList.value.items.length)
  savedReorderPageLengths.value = [...reorderPageLengths.value]
  clearReorderMoveNotice()
}

function listItemRatingStarClass(item) {
  return [
    'rating-star',
    normalizeRatingValue(item.show.rating) ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function listItemRatingText(item) {
  const normalized = normalizeRatingValue(item.show.rating)
  return normalized ? `${normalized} / 10` : '/ 10'
}

function openRatingPicker(item) {
  if (!isAuthenticated.value || !item?.show?.slug) {
    return
  }
  selectedRatingItem.value = item
  ratingPreview.value = 0
}

function closeRatingPicker() {
  selectedRatingItem.value = null
  ratingPreview.value = 0
}

function previewRating(star) {
  ratingPreview.value = star
}

function resetRatingPreview() {
  ratingPreview.value = 0
}

function ratingPickerStarClass(star) {
  const currentRating = normalizeRatingValue(selectedRatingItem.value?.show?.rating) || 0
  const visibleRating = ratingPreview.value || currentRating
  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

async function toggleListItemWatchlist(item) {
  const slug = item?.show?.slug
  if (!slug || !isAuthenticated.value || showWatchlistPendingBySlug.value[slug]) {
    return
  }

  showWatchlistPendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/shows/${slug}/watchlist`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'B' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.show.watchlisted = Boolean(data.watchlisted)
  } catch {
    // Leave the local watchlist state unchanged on request failure.
  } finally {
    showWatchlistPendingBySlug.value[slug] = false
  }
}

async function toggleListItemFavourite(item) {
  const slug = item?.show?.slug
  if (!slug || !isAuthenticated.value || showFavouritePendingBySlug.value[slug]) {
    return
  }

  showFavouritePendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/shows/${slug}/favourite`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'B' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.show.favourited = Boolean(data.favourited)
  } catch {
    // Leave the local favourite state unchanged on request failure.
  } finally {
    showFavouritePendingBySlug.value[slug] = false
  }
}

async function selectRating(ratingValue) {
  const item = selectedRatingItem.value
  const slug = item?.show?.slug
  if (!slug || !isAuthenticated.value || showRatingPendingBySlug.value[slug]) {
    return
  }

  showRatingPendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/shows/${slug}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'B' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    const data = await response.json()
    item.show.rating = normalizeRatingValue(data.rating)
    closeRatingPicker()
  } catch {
    // Leave the local rating unchanged on request failure.
  } finally {
    showRatingPendingBySlug.value[slug] = false
  }
}

async function resetSelectedItemRating() {
  const item = selectedRatingItem.value
  const slug = item?.show?.slug
  if (
    !slug
    || !isAuthenticated.value
    || !normalizeRatingValue(item?.show?.rating)
    || showRatingPendingBySlug.value[slug]
  ) {
    return
  }

  showRatingPendingBySlug.value[slug] = true
  try {
    const response = await fetch(`/api/v1/shows/${slug}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      return
    }
    item.show.rating = null
    closeRatingPicker()
  } catch {
    // Leave the local rating unchanged on request failure.
  } finally {
    showRatingPendingBySlug.value[slug] = false
  }
}

useMetaDescription(() => buildMetaDescription(
  'show-list',
  showListLoaded.value && !showListError.value ? { title: showList.value.title } : {},
))
</script>
